/* components/primitives.jsx — shared OS-shell primitives, markup ported
 * verbatim from the prototype helpers:
 *   OsTile     ← osTile   (HTML 54990)
 *   OsChip     ← .oschip/.osdot status chip markup
 *   OsBtn      ← .osbtn (brass variant)
 *   OsTable    ← osTbl   (HTML 54993)
 *   StatusChip ← pimStatusChip (HTML 54675-54685, exact title strings)
 *   M3dBadge   ← .m3d ok/no em-dash badge (HTML 54774)
 *   Toast      ← host toast, body portal under .catx-overlay (z-index 12000
 *                — the documented conscious fix of the prototype's hidden
 *                toast; strings come through store.notify verbatim). */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useCatalog } from "../store";
import { useT } from "../../i18n";

/* osTile(span, eyebrow, html) — 12-col dashboard tile. */
export function OsTile({ span, eyebrow, children, style, ...rest }) {
  return (
    <div className="ostile" style={{ gridColumn: `span ${span}`, ...(style || {}) }} {...rest}>
      <div className="osey" style={{ marginBottom: 9 }}>{eyebrow}</div>
      {children}
    </div>
  );
}

/* Generic .oschip — optional status dot (cls: ok|idle|warn|bad|run). */
export function OsChip({ cls = "", dot, children, ...rest }) {
  return (
    <span className={"oschip" + (cls ? " " + cls : "")} {...rest}>
      {dot != null ? <span className={"osdot " + dot} /> : null}
      {children}
    </span>
  );
}

/* .osbtn — brass = the highlighted variant (e.g. "+ New colour"). */
export function OsBtn({ brass, className = "", children, ...rest }) {
  return (
    <button className={"osbtn" + (brass ? " brass" : "") + (className ? " " + className : "")} {...rest}>
      {children}
    </button>
  );
}

/* osTbl(cols, rows) — table.dg with a simple header row; children = <tr>s. */
export function OsTable({ cols, children, style, ...rest }) {
  return (
    <table className="dg" style={{ width: "100%", ...(style || {}) }} {...rest}>
      <thead>
        <tr>{cols.map((c, i) => <th key={i}>{c}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

/* pimStatusChip(u, clickable) — live/not-live chip with exact title strings. */
export function StatusChip({ u, clickable, onToggle }) {
  const t = useT();
  const live = u.status === "live";
  const cls = live ? "ok" : "idle";
  const label = live ? t("tech.chip.live") : t("tech.chip.notLive");
  if (clickable) {
    const title = live ? "Live on frontend — click to take offline" : "Not live — click to make live";
    return (
      <span
        className={"oschip " + cls}
        data-livetoggle={u.sku}
        role="button"
        title={title}
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={onToggle}
      >
        <span className={"osdot " + cls} />{label}
      </span>
    );
  }
  return <span className={"oschip " + cls}><span className={"osdot " + cls} />{label}</span>;
}

/* .m3d ok/no — the em-dash 3D badge. */
export function M3dBadge({ ok }) {
  return <span className={"m3d " + (ok ? "ok" : "no")}>{"—"}</span>;
}

/* Toast host — reads ui.toast from the store, portals to <body> so it sits
 * above the fixed #lhcos shell (catx-overlay carries the dark tokens). The
 * last message is held during the fade-out transition. */
export function Toast() {
  const toast = useCatalog((s) => s.ui.toast);
  const [held, setHeld] = useState(null);
  useEffect(() => { if (toast) setHeld(toast); }, [toast]);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className={"catx-overlay toast" + (toast ? " show" : "")}>{held ? held.msg : ""}</div>,
    document.body
  );
}
